import { useState } from "react"

import { CommentsPanel, CommentForm } from "./Comments"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import { Comment, CommentStatus } from "types/comments.types"

interface EditorCommentsUIProps {
  /** All comments for the current variant */
  comments: Comment[]
  /** ID of the currently active comment */
  activeCommentId: string | null
  /** Whether the comments panel is open */
  isPanelOpen: boolean
  /** Whether the add-comment form is open */
  isFormOpen: boolean
  /** Text selected when the add-comment form was opened */
  formSelectedText: string
  /** Position of the add-comment form */
  formPosition: { x: number; y: number } | null
  /** Closes the comments panel */
  closePanel: () => void
  /** Closes the add-comment form */
  closeCommentForm: () => void
  /** Adds a comment for the current selection */
  addComment: (content: string) => void
  /** Updates a comment's content */
  updateComment: (commentId: string, content: string) => void
  /** Adds a reply to a comment */
  addReply: (commentId: string, content: string) => void
  /** Deletes a comment */
  deleteComment: (commentId: string) => void
  /** Deletes a reply from a comment */
  deleteReply: (commentId: string, replyId: string) => void
  /** Sets a comment's status */
  setCommentStatus: (commentId: string, status: CommentStatus) => void
  /** Resolves a comment */
  resolveComment: (commentId: string) => void
  /** Sets the active comment */
  setActiveComment: (commentId: string | null) => void
  /** Navigates the editor to a comment */
  navigateToComment: (commentId: string) => void
  /** IDs of comments that are new (for highlighting) */
  newCommentIds: Set<string>
  /** Exports the comments audit log */
  onExportAuditLog: () => void
  /** Ref to the editor container (for panel positioning) */
  editorContainerRef: React.RefObject<HTMLDivElement | null>
}

/**
 * EditorCommentsUI renders the comment overlay UI for the questionnaire
 * editor: the comments panel, the add-comment and reply forms, and the
 * delete-confirmation modals, together with their local modal state.
 * The useComments hook itself stays in the Editor component (the menu bar
 * and context menu consume its values too); this component only receives
 * the fields it renders.
 */
const EditorCommentsUI = ({
  comments,
  activeCommentId,
  isPanelOpen,
  isFormOpen,
  formSelectedText,
  formPosition,
  closePanel,
  closeCommentForm,
  addComment,
  updateComment,
  addReply,
  deleteComment,
  deleteReply,
  setCommentStatus,
  resolveComment,
  setActiveComment,
  navigateToComment,
  newCommentIds,
  onExportAuditLog,
  editorContainerRef,
}: EditorCommentsUIProps) => {
  // Comment action modal states
  const [replyModalState, setReplyModalState] = useState<{
    visible: boolean
    commentId: string | null
  }>({ visible: false, commentId: null })

  const [deleteCommentModalState, setDeleteCommentModalState] = useState<{
    visible: boolean
    commentId: string | null
  }>({ visible: false, commentId: null })

  const [deleteReplyModalState, setDeleteReplyModalState] = useState<{
    visible: boolean
    commentId: string | null
    replyId: string | null
  }>({ visible: false, commentId: null, replyId: null })

  // Comment action handlers
  const handleReplyClick = (commentId: string) => {
    setReplyModalState({ visible: true, commentId })
  }

  const handleReplySubmit = (content: string) => {
    if (replyModalState.commentId && content.trim()) {
      addReply(replyModalState.commentId, content.trim())
    }
    setReplyModalState({ visible: false, commentId: null })
  }

  const handleDeleteCommentClick = (commentId: string) => {
    setDeleteCommentModalState({ visible: true, commentId })
  }

  const handleDeleteCommentConfirm = () => {
    if (deleteCommentModalState.commentId) {
      deleteComment(deleteCommentModalState.commentId)
    }
    setDeleteCommentModalState({ visible: false, commentId: null })
  }

  const handleDeleteReplyClick = (commentId: string, replyId: string) => {
    setDeleteReplyModalState({ visible: true, commentId, replyId })
  }

  const handleDeleteReplyConfirm = () => {
    if (deleteReplyModalState.commentId && deleteReplyModalState.replyId) {
      deleteReply(
        deleteReplyModalState.commentId,
        deleteReplyModalState.replyId,
      )
    }
    setDeleteReplyModalState({ visible: false, commentId: null, replyId: null })
  }

  return (
    <>
      {/* Comments Panel */}
      <CommentsPanel
        isVisible={isPanelOpen}
        comments={comments}
        activeCommentId={activeCommentId}
        onClose={closePanel}
        onCommentSelect={setActiveComment}
        onCommentNavigate={navigateToComment}
        onCommentReply={handleReplyClick}
        onStatusChange={setCommentStatus}
        onCommentDelete={handleDeleteCommentClick}
        onCommentResolve={resolveComment}
        onCommentUpdate={updateComment}
        onReplyDelete={handleDeleteReplyClick}
        onExportAuditLog={onExportAuditLog}
        editorContainerRef={editorContainerRef}
        newCommentIds={newCommentIds}
      />

      {/* Comment Form */}
      <CommentForm
        isVisible={isFormOpen}
        selectedText={formSelectedText}
        position={formPosition}
        onSubmit={addComment}
        onCancel={closeCommentForm}
      />

      {/* Reply Form Modal */}
      <CommentForm
        isVisible={replyModalState.visible}
        selectedText=""
        onSubmit={handleReplySubmit}
        onCancel={() => setReplyModalState({ visible: false, commentId: null })}
        isReply={true}
        placeholder="Write your reply..."
      />

      {/* Delete Comment Confirmation Modal */}
      <ConfirmationModal
        title="Delete Comment"
        isVisible={deleteCommentModalState.visible}
        message="Are you sure you want to delete this comment? This action cannot be undone."
        cancel={() =>
          setDeleteCommentModalState({ visible: false, commentId: null })
        }
        confirm={handleDeleteCommentConfirm}
        confirmButtonText="Delete"
        confirmButtonType="danger"
      />

      {/* Delete Reply Confirmation Modal */}
      <ConfirmationModal
        title="Delete Reply"
        isVisible={deleteReplyModalState.visible}
        message="Are you sure you want to delete this reply? This action cannot be undone."
        cancel={() =>
          setDeleteReplyModalState({
            visible: false,
            commentId: null,
            replyId: null,
          })
        }
        confirm={handleDeleteReplyConfirm}
        confirmButtonText="Delete"
        confirmButtonType="danger"
      />
    </>
  )
}

export default EditorCommentsUI
